import React, { ReactNode, useState } from 'react';
import { Box, Button, Checkbox, List, ListItem, ListItemText, Popover } from '@mui/material';
import ApplicationsSearch from './SearchField';
import { FilterButton } from './FilterButton';
import { SkeletonList } from './SkeletonList';

export type OptionType = {
  id: string;
  marketingName: string;
  selected: boolean;
};

type Props = {
  id: string;
  search: string;
  isLoading: boolean;
  numberOfSelectedOptions: number;
  options: OptionType[];
  title: ReactNode | string;
  visibleOptions: OptionType[];
  onOpen: () => void;
  onClose: () => void;
  onOptionsClear: () => void;
  onVisibleOptionsClear: () => void;
  onAllVisibleOptionsSelect: () => void;
  onSearch: (searchValue: string) => void;
  onOptionSelect: (selectedOption: OptionType) => void;
};

export function SearchableFilterField(props: Props) {
  const {
    id,
    title,
    search,
    options,
    isLoading,
    visibleOptions,
    numberOfSelectedOptions,
    onOpen,
    onClose,
    onSearch,
    onOptionSelect,
    onAllVisibleOptionsSelect,
    onVisibleOptionsClear,
    onOptionsClear,
  } = props;

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const selectedOptions = options.filter((option) => option.selected);

  const selectedVisibleOptions = visibleOptions.filter((visibleOption) => visibleOption.selected);
  const isAllVisibleSelected = selectedVisibleOptions.length === visibleOptions.length;

  const areSomeVisibleOptionsChecked =
    selectedVisibleOptions.length > 0 && selectedVisibleOptions.length < visibleOptions.length;

  const sortedOptions = visibleOptions.toSorted((a, _) => (a.selected ? -1 : 1));

  function handleButtonClick(event: React.MouseEvent<HTMLButtonElement>) {
    setAnchorEl(event.currentTarget);
    onOpen();
  }

  function handleClose() {
    setAnchorEl(null);
    onClose();
  }

  function handleSelectAll() {
    if (isAllVisibleSelected) {
      onVisibleOptionsClear();

      return;
    }

    onAllVisibleOptionsSelect();
  }

  function handleOptionToggle(selectedOption: OptionType) {
    onOptionSelect(selectedOption);
  }

  return (
    <>
      <FilterButton
        id={id}
        anchorEl={anchorEl}
        onClick={handleButtonClick}
        numberOfSelectedOptions={numberOfSelectedOptions}
      >
        {title}
      </FilterButton>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        sx={{
          boxShadow: '4px 16px 32px 0px rgba(29, 29, 29, 0.08)',
          borderRadius: '4px',
        }}
      >
        <Box data-testid={`${id}-menu`}>
          <ListItem
            data-testid="filter-menu-search-item"
            sx={{
              p: 0,
              px: 1.5,
              pt: 1.5,
            }}
          >
            <ApplicationsSearch label="Search properties" search={search} onChange={onSearch} />
          </ListItem>
          <List
            sx={{
              maxHeight: 350,
              minHeight: 200,
              maxWidth: 528,
              minWidth: 400,
              overflowY: 'auto',
              pb: 0,
              cursor: 'pointer',
            }}
          >
            {isLoading ? (
              <SkeletonList numberOfItems={10} />
            ) : (
              <>
                <ListItem
                  sx={{ p: 0, pr: 2 }}
                  data-testid="select-all-filter-menu-item"
                  onClick={handleSelectAll}
                >
                  <Checkbox
                    checked={isAllVisibleSelected}
                    indeterminate={areSomeVisibleOptionsChecked}
                    data-testid={`${id}_checkbox_all`}
                  />
                  <ListItemText
                    primary="Select All"
                    data-testid={`${id}_all`}
                    sx={{ my: 1, mx: 0 }}
                  />
                </ListItem>
                <Box>
                  <Box>
                    {sortedOptions.map((option) => {
                      const checked = selectedOptions.some((selected) => selected.id === option.id);

                      return (
                        <ListItem
                          sx={{ p: 0, pr: 2 }}
                          key={option.id}
                          data-testid="filter-menu-item"
                          data-checked={checked}
                          onClick={() => handleOptionToggle(option)}
                        >
                          <Checkbox checked={checked} data-testid={`${id}-checkbox`} />
                          <ListItemText
                            primary={option.marketingName}
                            data-testid={`${id}-item`}
                            sx={{ my: 1, mx: 0 }}
                          />
                        </ListItem>
                      );
                    })}
                  </Box>
                </Box>
              </>
            )}
          </List>
          <Button data-testid={`${id}_clear`} variant="text" onClick={onOptionsClear}>
            Clear all
          </Button>
        </Box>
      </Popover>
    </>
  );
}
